/*
 * Copyright (c) 2026.
 * This file is part of the Compo B2B application.
 * Compo soft - Dmitri Puscas <degwelloa@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  ApiResponse,
  BaseComponent,
  CartProduct,
  CartRemoteService,
  Contract,
  CustomerGroup,
  DeliveryAddress,
  OrderForm,
  OrderInfo,
  OrderService,
  PickupAddress,
  SourceOrderProduct,
  StoreService
} from 'compob2b-libs';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

/**
 * Inline order block rendered beside the cart table.
 *
 * <p>Replaces the former "Запросить счёт" popup. Choosing a delivery address emits
 * {@link deliveryAddressChange} so the cart can be re-priced under that address's contract; the
 * switch is cart-scoped — this component never writes the stored customer, the session or
 * localStorage, and never calls {@code PATCH /customer/set-default-contract}.
 */
@Component({
  selector: 'app-cart-order-block',
  templateUrl: './cart-order-block.component.html',
  styleUrls: ['./cart-order-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CartOrderBlockComponent extends BaseComponent implements OnInit {

  /** Rows currently selected in the cart table — these are what the invoice is requested for. */
  @Input() products: CartProduct[] = [];
  @Input() totalCost = 0;
  @Input() totalCostVAT = 0;
  @Input() totalAmount = 0;
  @Input() canEdit = true;

  /** Emits the delivery address whose contract the cart must be priced under, or null for ambient. */
  @Output() deliveryAddressChange = new EventEmitter<number | null>();
  @Output() exportXls = new EventEmitter<void>();

  /**
   * True while the confirmation modal is shown. The parent's cart-empty guards (mobile-view's
   * *ngIf on cartProductList.length) would otherwise unmount this component the instant the
   * placed order's rows are removed from the cart — the confirmation must stay visible until
   * the customer closes it or navigates away.
   */
  @Output() orderConfirmationVisibleChange = new EventEmitter<boolean>();

  static readonly PICKUP = '2';
  static readonly DELIVERY = '1';

  fail = false;
  submitted = false;
  orderInfo: OrderInfo;
  showValidation = false;

  pickupAddresses$: Observable<PickupAddress[]>;
  deliveryAddresses$: Observable<DeliveryAddress[]>;

  pickupAddresses: PickupAddress[] = [];
  deliveryAddresses: DeliveryAddress[] = [];

  inputForm = new UntypedFormGroup({
    deliveryMethod: new UntypedFormControl(CartOrderBlockComponent.DELIVERY),
    pickupAddress: new UntypedFormControl(null),
    deliveryAddress: new UntypedFormControl(null),
    comment: new UntypedFormControl(null)
  });

  readonly customerGroup$ = this.storeService.getCustomerGroup();

  constructor(private readonly storeService: StoreService,
              private readonly orderService: OrderService,
              private readonly cartService: CartRemoteService) {
    super();
  }

  ngOnInit(): void {
    this.pickupAddresses$ = this.customerGroup$.pipe(
      switchMap(response => this.getPickupAddresses(response)),
      tap(addressList => this.preselectPickupAddress(addressList)),
      shareReplay()
    );

    // All of the customer's addresses, across every contract — the backend already excludes
    // addresses without a contract, which could not resolve a price.
    this.deliveryAddresses$ = this.orderService.getDeliveryAddresses().pipe(
      map(response => response.data),
      tap(addressList => this.handleDeliveryAddresses(addressList)),
      shareReplay()
    );
  }

  private getPickupAddresses(customerGroup: CustomerGroup): Observable<PickupAddress[]> {
    return this.orderService.getPickupAddresses(customerGroup).pipe(map(response => response.data));
  }

  private preselectPickupAddress(addressList: PickupAddress[]): void {
    this.pickupAddresses = addressList ?? [];

    if (this.pickupAddresses.length > 0 && !this.inputForm.controls['pickupAddress'].value) {
      this.inputForm.controls['pickupAddress'].setValue(this.pickupAddresses[0].id);
      this.cdr.detectChanges();
    }
  }

  private handleDeliveryAddresses(addressList: DeliveryAddress[]): void {
    this.deliveryAddresses = addressList ?? [];

    if (this.preselectDeliveryAddress()) {
      this.emitContractContext();
    }
    this.cdr.detectChanges();
  }

  /**
   * Exactly one alive address: preselect it and let the template disable the control.
   *
   * @return whether it actually wrote the control — the caller owns the contract-context emit, so
   *         that resetting the form emits once rather than twice.
   */
  private preselectDeliveryAddress(): boolean {
    if (this.deliveryAddresses.length === 1 && !this.inputForm.controls['deliveryAddress'].value) {
      this.inputForm.controls['deliveryAddress'].setValue(this.deliveryAddresses[0].id);
      return true;
    }
    return false;
  }

  /** True when the address select must be rendered disabled — a single choice is not a choice. */
  get singleAddress(): boolean {
    return this.deliveryAddresses.length === 1;
  }

  get isPickup(): boolean {
    return this.inputForm.value.deliveryMethod === CartOrderBlockComponent.PICKUP;
  }

  get isDelivery(): boolean {
    return this.inputForm.value.deliveryMethod === CartOrderBlockComponent.DELIVERY;
  }

  get selectedDeliveryAddressId(): number | null {
    const value = this.inputForm.controls['deliveryAddress'].value;
    return value === null || value === undefined ? null : Number(value);
  }

  /**
   * The contract the block currently describes: the one carried by the chosen delivery address,
   * or the ambient contract when delivering nowhere in particular (pickup, or nothing chosen).
   */
  get contract(): Contract | null {
    const customer = this.storeService.getCustomer();
    const contracts = customer?.contracts ?? [];

    let contractId = customer?.contractId;

    if (this.isDelivery && this.selectedDeliveryAddressId !== null) {
      const address = this.deliveryAddresses.find(item => item.id === this.selectedDeliveryAddressId);
      if (address?.contractId) {
        contractId = address.contractId;
      }
    }

    return contracts.find(item => item.id === contractId) ?? null;
  }

  /**
   * @param method the newly chosen method, taken from the event rather than from the form control:
   *               nb-radio-group emits valueChange before the reactive control has been written, so
   *               reading the control here would act on the previous method.
   */
  onDeliveryMethodChange(method: string): void {
    // Pickup points hang off customer_group, not off a contract, so pickup returns the cart to the
    // ambient contract.
    this.deliveryAddressChange.emit(
      method === CartOrderBlockComponent.DELIVERY ? this.selectedDeliveryAddressId : null);
  }

  onDeliveryAddressChange(): void {
    this.emitContractContext();
  }

  private emitContractContext(): void {
    this.deliveryAddressChange.emit(this.isDelivery ? this.selectedDeliveryAddressId : null);
  }

  /** True when the form carries everything the backend needs for the chosen delivery method. */
  validForm(): boolean {
    const method = this.inputForm.controls['deliveryMethod'].value;
    if (method === CartOrderBlockComponent.PICKUP) {
      return !!this.inputForm.controls['pickupAddress'].value;
    }
    if (method === CartOrderBlockComponent.DELIVERY) {
      return !!this.inputForm.controls['deliveryAddress'].value;
    }
    return false;
  }

  get missingPickupAddress(): boolean {
    return this.showValidation && this.isPickup && !this.inputForm.controls['pickupAddress'].value;
  }

  get missingDeliveryAddress(): boolean {
    return this.showValidation && this.isDelivery && !this.inputForm.controls['deliveryAddress'].value;
  }

  get noProducts(): boolean {
    return this.showValidation && !this.products?.length;
  }

  onSubmit(): void {
    this.showValidation = true;

    // Inline error only — nothing is sent until the form is complete.
    if (!this.validForm() || !this.products?.length) {
      this.cdr.detectChanges();
      return;
    }

    this.submitted = true;

    const pickup = this.isPickup;
    // `products` is re-bound on every render — pin the rows actually sent, they are the ones to drop from the cart.
    const orderedProducts = this.products;

    const orderForm: OrderForm = {
      deliveryMethod: parseInt(this.inputForm.value.deliveryMethod, 10),
      // Send only the address the chosen method actually uses; the other one stays 0.
      deliveryAddress: pickup ? 0 : Number(this.inputForm.value.deliveryAddress ?? 0),
      pickupAddress: pickup ? Number(this.inputForm.value.pickupAddress ?? 0) : 0,
      comment: this.inputForm.value.comment,
      source: SourceOrderProduct.ORDER_PART,
      sourceId: null,
      cart: orderedProducts.map(item => item.id)
    };

    this.orderService.create(orderForm).pipe(
      tap((response: ApiResponse<OrderInfo>) => this.handleResponse(response, orderedProducts)),
      take(1),
      catchError(err => this.handleError(err, true))
    ).subscribe();
  }

  private handleResponse(response: ApiResponse<OrderInfo>, orderedProducts: CartProduct[]): void {
    if (response.success) {
      this.fail = false;
      this.orderInfo = response.data;
      this.orderConfirmationVisibleChange.emit(true);
      orderedProducts.forEach(item => this.cartService.remove(item).pipe(take(1)).subscribe());
    } else {
      this.fail = true;
    }

    this.submitted = false;
    this.cdr.detectChanges();
  }

  onExportXls(): void {
    this.exportXls.emit();
  }

  goToOrder(): void {
    this.router.navigate(['cabinet', 'reports', 'orders', String(this.orderInfo.id)]);
    this.orderConfirmationVisibleChange.emit(false);
  }

  /** Closes the confirmation modal, returning to the (now-empty) order form. */
  closeConfirmation(): void {
    this.orderInfo = null;
    this.resetForm();
    this.orderConfirmationVisibleChange.emit(false);
  }

  /**
   * Returns the form to the state a freshly mounted block carries: default delivery method, no
   * comment, and the same address preselections `ngOnInit` applies.
   *
   * <p>The form is only hidden behind the confirmation (`*ngIf="!orderInfo"` in the template), never
   * re-created, so without this it comes back holding the placed order's input and the next order
   * starts pre-filled from the last one.
   *
   * <p>`reset()` also clears touched/dirty, and the contract context is re-emitted at the end: the
   * cart is still priced under the address the placed order used, which may no longer be the one the
   * reset form shows.
   */
  private resetForm(): void {
    this.fail = false;
    this.showValidation = false;

    this.inputForm.reset({
      deliveryMethod: CartOrderBlockComponent.DELIVERY,
      pickupAddress: null,
      deliveryAddress: null,
      comment: null
    });

    this.preselectPickupAddress(this.pickupAddresses);
    this.preselectDeliveryAddress();
    this.emitContractContext();
  }
}
